/**
 * src/constants/appModules.js
 *
 * SINGLE SOURCE OF TRUTH for the app's permission model.
 *
 * Each entry maps a UI module to:
 *  - key        → the permission object key stored in user.permissions (e.g. 'hrms_employees')
 *  - actions    → which action flags are supported ('view','add','edit','delete')
 *  - route      → the React-Router path for this module
 *  - navGroup   → which top-level nav section this belongs to (used for group-gating)
 *  - navGroupKey→ the permission key that gates the entire nav section
 *  - label      → human-readable name
 *
 * Adding a new module:
 *  1. Add an entry here.
 *  2. Add it to DESIGNATION_PERMISSION_MATRIX (designationPermissionMatrix.js).
 *  3. Add a permKey to the route in App.jsx.
 *  4. Add a permKey to the nav item in TopNav.jsx.
 *  No other file needs to change.
 */

/** Actions supported by a full CRUD module */
const CRUD = ['view', 'add', 'edit', 'delete'];
/** Read-only module — only view */
const VIEW_ONLY = ['view'];

// ─── NAV GROUP KEYS ────────────────────────────────────────────────────────────
// These keys gate the entire dropdown / top-level nav item.
// A user without `permissions['clinical'].view` won't see the Clinical nav group.
export const NAV_GROUP_KEYS = {
  DASHBOARD:  'dashboard',
  CLINICAL:   'clinical',
  HRMS:       'hrms',
  DRUGS:      'drugs',
  CITIZENS:   'citizens',
  REPORTS:    'reports',
  IOT:        'iot',
  SYNC:       'sync',
  INSPECTIONS:'inspections',
  SETTINGS:   'settings',
};

// ─── MODULE DEFINITIONS ────────────────────────────────────────────────────────
export const APP_MODULES = [
  // ── Dashboard ──────────────────────────────────────────────────────────────
  {
    key: 'dashboard',
    label: 'Dashboard',
    actions: VIEW_ONLY,
    route: '/dashboard',
    navGroup: 'Dashboard',
    navGroupKey: NAV_GROUP_KEYS.DASHBOARD,
  },
  {
    key: 'daily_progress',
    label: 'Daily Progress',
    actions: VIEW_ONLY,
    route: '/daily-progress',
    navGroup: 'Dashboard',
    navGroupKey: NAV_GROUP_KEYS.DASHBOARD,
  },

  // ── Clinical ───────────────────────────────────────────────────────────────
  {
    key: 'clinical_cases',
    label: 'Cases Treated',
    actions: CRUD,
    route: '/cases',
    navGroup: 'Clinical',
    navGroupKey: NAV_GROUP_KEYS.CLINICAL,
  },
  {
    key: 'clinical_vaccinations',
    label: 'Vaccinations',
    actions: CRUD,
    route: '/vaccinations',
    navGroup: 'Clinical',
    navGroupKey: NAV_GROUP_KEYS.CLINICAL,
  },
  {
    key: 'clinical_deworming',
    label: 'Deworming',
    actions: CRUD,
    route: '/deworming',
    navGroup: 'Clinical',
    navGroupKey: NAV_GROUP_KEYS.CLINICAL,
  },
  {
    key: 'clinical_ai_service',
    label: 'Artificial Insemination',
    actions: CRUD,
    route: '/ai-service',
    navGroup: 'Clinical',
    navGroupKey: NAV_GROUP_KEYS.CLINICAL,
  },
  {
    key: 'fodder',
    label: 'Fodder',
    actions: CRUD,
    route: '/fodder',
    navGroup: 'Clinical',
    navGroupKey: NAV_GROUP_KEYS.CLINICAL,
  },

  // ── HRMS ───────────────────────────────────────────────────────────────────
  {
    key: 'hrms_overview',
    label: 'HRMS Overview',
    actions: VIEW_ONLY,
    route: '/hrms',
    navGroup: 'HRMS',
    navGroupKey: NAV_GROUP_KEYS.HRMS,
  },
  {
    key: 'hrms_employees',
    label: 'Employees',
    actions: CRUD,
    route: '/employees',
    navGroup: 'HRMS',
    navGroupKey: NAV_GROUP_KEYS.HRMS,
  },
  {
    key: 'hrms_attendance',
    label: 'Attendance',
    actions: CRUD,
    route: '/attendance',
    navGroup: 'HRMS',
    navGroupKey: NAV_GROUP_KEYS.HRMS,
  },
  {
    key: 'hrms_attendance_approve',
    label: 'Attendance Approve',
    actions: CRUD,
    route: '/attendance-approve',
    navGroup: 'HRMS',
    navGroupKey: NAV_GROUP_KEYS.HRMS,
  },
  {
    key: 'hrms_attendance_report',
    label: 'Attendance Report',
    actions: VIEW_ONLY,
    route: '/attendance-report',
    navGroup: 'HRMS',
    navGroupKey: NAV_GROUP_KEYS.HRMS,
  },
  {
    key: 'hrms_leave',
    label: 'Leave Management',
    actions: CRUD,
    route: '/leaves',
    navGroup: 'HRMS',
    navGroupKey: NAV_GROUP_KEYS.HRMS,
  },
  {
    key: 'hrms_farmers',
    label: 'Farmers',
    actions: CRUD,
    route: '/farmers',
    navGroup: 'HRMS',
    navGroupKey: NAV_GROUP_KEYS.HRMS,
  },

  // ── Drugs ──────────────────────────────────────────────────────────────────
  {
    key: 'drugs_products',
    label: 'Products (Drugs)',
    actions: CRUD,
    route: '/drugs',
    navGroup: 'Drugs',
    navGroupKey: NAV_GROUP_KEYS.DRUGS,
  },
  {
    key: 'drugs_stock',
    label: 'Stock / Purchases',
    actions: CRUD,
    route: '/stock',
    navGroup: 'Drugs',
    navGroupKey: NAV_GROUP_KEYS.DRUGS,
  },
  {
    key: 'drugs_indents',
    label: 'Drug Indents',
    actions: CRUD,
    route: '/indents',
    navGroup: 'Drugs',
    navGroupKey: NAV_GROUP_KEYS.DRUGS,
  },
  {
    key: 'drugs_allocations',
    label: 'Allocations',
    actions: CRUD,
    route: '/allocations',
    navGroup: 'Drugs',
    navGroupKey: NAV_GROUP_KEYS.DRUGS,
  },
  {
    key: 'drugs_sale',
    label: 'Drug Sale',
    actions: CRUD,
    route: '/drug-sale',
    navGroup: 'Drugs',
    navGroupKey: NAV_GROUP_KEYS.DRUGS,
  },

  // ── Citizens ───────────────────────────────────────────────────────────────
  {
    key: 'citizens_registration',
    label: 'Citizen Registration',
    actions: CRUD,
    route: '/citizen/registration',
    navGroup: 'Citizens',
    navGroupKey: NAV_GROUP_KEYS.CITIZENS,
  },
  {
    key: 'citizens_fodder_seed',
    label: 'Fodder Seed',
    actions: CRUD,
    route: '/citizen/fodder-seed',
    navGroup: 'Citizens',
    navGroupKey: NAV_GROUP_KEYS.CITIZENS,
  },
  {
    key: 'citizens_sss',
    label: 'Sex Sorted Semen',
    actions: CRUD,
    route: '/citizen/sex-sorted-semen',
    navGroup: 'Citizens',
    navGroupKey: NAV_GROUP_KEYS.CITIZENS,
  },
  {
    key: 'citizens_deworming',
    label: 'Citizen Deworming',
    actions: CRUD,
    route: '/citizen/deworming',
    navGroup: 'Citizens',
    navGroupKey: NAV_GROUP_KEYS.CITIZENS,
  },
  {
    key: 'citizens_ai',
    label: 'Artificial Insemination (Citizen)',
    actions: CRUD,
    route: '/citizen/ai',
    navGroup: 'Citizens',
    navGroupKey: NAV_GROUP_KEYS.CITIZENS,
  },
  {
    key: 'citizens_grievances',
    label: 'Grievances',
    actions: CRUD,
    route: '/citizen/grievances',
    navGroup: 'Citizens',
    navGroupKey: NAV_GROUP_KEYS.CITIZENS,
  },

  // ── Reports ────────────────────────────────────────────────────────────────
  {
    key: 'reports',
    label: 'Reports (section nav)',
    actions: VIEW_ONLY,
    route: '/reports',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },

  // Combined Attendance Reports
  {
    key: 'reports_daily_attendance',
    label: 'Daily Attendance Report',
    actions: VIEW_ONLY,
    route: '/reports/daily-attendance',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_daily_abstract',
    label: 'Daily Attendance Abstract',
    actions: VIEW_ONLY,
    route: '/reports/daily-abstract',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_attendance_abstract',
    label: 'Attendance Abstract',
    actions: VIEW_ONLY,
    route: '/reports/attendance-abstract',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_range_attendance',
    label: 'Range Attendance Report',
    actions: VIEW_ONLY,
    route: '/reports/range-attendance',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },

  // Daily Abstracts
  {
    key: 'reports_district_common_abstract',
    label: 'Districts Abstract',
    actions: VIEW_ONLY,
    route: '/reports/district-common-abstract',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },

  // Clinical Reports
  {
    key: 'reports_cases',
    label: 'Case Treated Report',
    actions: VIEW_ONLY,
    route: '/reports/cases',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_ai',
    label: 'Artificial Insemination Report',
    actions: VIEW_ONLY,
    route: '/reports/ai',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_farmers',
    label: 'Farmers Report',
    actions: VIEW_ONLY,
    route: '/reports/farmers',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_fodder',
    label: 'Fodder Report',
    actions: VIEW_ONLY,
    route: '/reports/fodder',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },

  // Inventory & Inventory Reports
  {
    key: 'reports_inventory',
    label: 'Inventory Management Report',
    actions: VIEW_ONLY,
    route: '/reports/inventory',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_drug_wise',
    label: 'Drug-wise Issuance Report',
    actions: VIEW_ONLY,
    route: '/reports/drug-wise',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },

  // Drug-Indent Reports
  {
    key: 'reports_drug_indent_district_wise',
    label: 'District Wise Drug Report',
    actions: VIEW_ONLY,
    route: '/reports/drug-indent/district-wise',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_drug_indent_group_wise',
    label: 'Group Wise District Indent Report',
    actions: VIEW_ONLY,
    route: '/reports/drug-indent/group-wise',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_drug_indent_district_wise_abstract',
    label: 'District Wise Abstract Report',
    actions: VIEW_ONLY,
    route: '/reports/drug-indent/district-wise-abstract',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_drug_indent_district_wise_institution_count',
    label: 'District Wise Institution Count Report',
    actions: VIEW_ONLY,
    route: '/reports/drug-indent/district-wise-institution-count',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_drug_indent_institution_wise',
    label: 'Institution-wise Drug Report',
    actions: VIEW_ONLY,
    route: '/reports/drug-indent/institution-wise',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_drug_indent_institution_progress',
    label: 'Institution Progress Reports',
    actions: VIEW_ONLY,
    route: '/reports/drug-indent/institution-progress',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_drug_indent_district_wise_detailed',
    label: 'District Wise Detailed Drug Report',
    actions: VIEW_ONLY,
    route: '/reports/drug-indent/district-wise-detailed',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },

  // Drug-Indent Reports (FY 2026-27)
  {
    key: 'reports_drug_indent_26_27_district_wise',
    label: 'District Wise Drug Report (26-27)',
    actions: VIEW_ONLY,
    route: '/reports/drug-indent-26-27/district-wise',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_drug_indent_26_27_group_wise',
    label: 'Group Wise District Indent Report (26-27)',
    actions: VIEW_ONLY,
    route: '/reports/drug-indent-26-27/group-wise',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_drug_indent_26_27_district_wise_abstract',
    label: 'District Wise Abstract Report (26-27)',
    actions: VIEW_ONLY,
    route: '/reports/drug-indent-26-27/district-wise-abstract',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_drug_indent_26_27_district_wise_institution_count',
    label: 'District Wise Institution Count Report (26-27)',
    actions: VIEW_ONLY,
    route: '/reports/drug-indent-26-27/district-wise-institution-count',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_drug_indent_26_27_institution_wise',
    label: 'Institution-wise Drug Report (26-27)',
    actions: VIEW_ONLY,
    route: '/reports/drug-indent-26-27/institution-wise',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_drug_indent_26_27_institution_progress',
    label: 'Institution Progress Reports (26-27)',
    actions: VIEW_ONLY,
    route: '/reports/drug-indent-26-27/institution-progress',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },
  {
    key: 'reports_drug_indent_26_27_district_wise_detailed',
    label: 'District Wise Detailed Drug Report (26-27)',
    actions: VIEW_ONLY,
    route: '/reports/drug-indent-26-27/district-wise-detailed',
    navGroup: 'Reports',
    navGroupKey: NAV_GROUP_KEYS.REPORTS,
  },

  // ── IoT ────────────────────────────────────────────────────────────────────
  {
    key: 'iot',
    label: 'IoT',
    actions: VIEW_ONLY,
    route: '/iot',
    navGroup: 'IoT',
    navGroupKey: NAV_GROUP_KEYS.IOT,
  },

  // ── Sync ───────────────────────────────────────────────────────────────────
  {
    key: 'sync',
    label: 'Data Sync',
    actions: VIEW_ONLY,
    route: '/sync',
    navGroup: 'Sync',
    navGroupKey: NAV_GROUP_KEYS.SYNC,
  },

  // ── Inspections ────────────────────────────────────────────────────────────
  {
    key: 'inspections',
    label: 'Inspections',
    actions: CRUD,
    route: '/inspections',
    navGroup: 'Inspections',
    navGroupKey: NAV_GROUP_KEYS.INSPECTIONS,
  },

  // ── Settings ───────────────────────────────────────────────────────────────
  {
    key: 'settings',
    label: 'Settings',
    actions: CRUD,
    route: '/settings',
    navGroup: 'Settings',
    navGroupKey: NAV_GROUP_KEYS.SETTINGS,
  },
];

/** Quick lookup: moduleKey → APP_MODULES entry */
export const MODULE_BY_KEY = Object.fromEntries(APP_MODULES.map((m) => [m.key, m]));

/** All unique module keys — useful for building the full designation permission matrix */
export const ALL_MODULE_KEYS = APP_MODULES.map((m) => m.key);

/**
 * Full permissions object granting every action on every module.
 * Used for demo director sessions and admin bypass.
 */
export const FULL_PERMISSIONS = Object.fromEntries(
  APP_MODULES.map((m) => [
    m.key,
    Object.fromEntries(m.actions.map((a) => [a, true])),
  ])
);

// Also include top-level nav group keys in FULL_PERMISSIONS so group gating passes
Object.values(NAV_GROUP_KEYS).forEach((key) => {
  if (!FULL_PERMISSIONS[key]) {
    FULL_PERMISSIONS[key] = { view: true };
  }
});
